import { Directive, Input, IterableDiffers, TemplateRef } from '@angular/core';
import {
  ElCdkCellDef,
  ElCdkFooterCellDef,
  ElCdkFooterRowDef,
  ElCdkHeaderCellDef,
  ElCdkHeaderRowDef,
  ElCdkRowDef,
} from '../cdk/table/type-mappings';
import { ElCellDefDirective, ElFooterCellDefDirective, ElHeaderCellDefDirective } from '../cdk/table/cell';
import { ElFooterRowDefDirective, ElHeaderRowDefDirective, ElRowDefDirective } from '../cdk/table/row';
import { ElColumnsService } from './tree-grid-columns.service';

export interface ElTreeGridResponsiveRowDef {
  hideColumn(column: string);
  showColumn(column: string);
}

/**
 * Data row definition for the tree-grid.
 * Captures the header row's template and columns to display.
 */
@Directive({
  selector: '[elTreeGridRowDef]',
  providers: [{ provide: ElCdkRowDef, useExisting: ElTreeGridRowDefDirective }],
})
export class ElTreeGridRowDefDirective<T> extends ElRowDefDirective<T> implements ElTreeGridResponsiveRowDef {
  /**
   * Columns to be displayed on this row
   */
  @Input('elTreeGridRowDefColumns')
  set columns(value: Iterable<string>) {
    this.columnsService.setColumns(value)
  }
  get columns(): Iterable<string> {
    return this.columnsService.getVisibleColumns();
  }

  constructor(
    template: TemplateRef<any>,
    differs: IterableDiffers,
    private columnsService: ElColumnsService,
  ) {
    super(template, differs);
  }

  /** @docs-private */
  hideColumn(column: string): void {
    this.columnsService.hideColumn(column);
  }

  /** @docs-private */
  showColumn(column: string): void {
    this.columnsService.showColumn(column);
  }
}

@Directive({
  selector: '[elTreeGridHeaderRowDef]',
  providers: [{ provide: ElCdkHeaderRowDef, useExisting: ElTreeGridHeaderRowDefDirective }],
})
export class ElTreeGridHeaderRowDefDirective extends ElHeaderRowDefDirective implements ElTreeGridResponsiveRowDef {
  /**
   * Columns to be displayed on this row
   */
  @Input('elTreeGridHeaderRowDef')
  set columns(value: Iterable<string>) {
    this.columnsService.setColumns(value)
  }
  get columns(): Iterable<string> {
    return this.columnsService.getVisibleColumns();
  }

  constructor(
    template: TemplateRef<any>,
    differs: IterableDiffers,
    private columnsService: ElColumnsService,
  ) {
    super(template, differs);
  }

  /** @docs-private */
  hideColumn(column: string): void {
    this.columnsService.hideColumn(column);
  }

  /** @docs-private */
  showColumn(column: string): void {
    this.columnsService.showColumn(column);
  }
}

@Directive({
  selector: '[elTreeGridFooterRowDef]',
  providers: [{ provide: ElCdkFooterRowDef, useExisting: ElTreeGridFooterRowDefDirective }],
})
export class ElTreeGridFooterRowDefDirective extends ElFooterRowDefDirective implements ElTreeGridResponsiveRowDef {
  /**
   * Columns to be displayed on this row
   */
  @Input('elTreeGridFooterRowDef')
  set columns(value: Iterable<string>) {
    this.columnsService.setColumns(value)
  }
  get columns(): Iterable<string> {
    return this.columnsService.getVisibleColumns();
  }

  constructor(
    template: TemplateRef<any>,
    differs: IterableDiffers,
    private columnsService: ElColumnsService,
  ) {
    super(template, differs);
  }

  /** @docs-private */
  hideColumn(column: string): void {
    this.columnsService.hideColumn(column);
  }

  /** @docs-private */
  showColumn(column: string): void {
    this.columnsService.showColumn(column);
  }
}

/**
 * Cell definition for a el-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
@Directive({
  selector: '[elTreeGridCellDef]',
  providers: [{ provide: ElCdkCellDef, useExisting: ElTreeGridCellDefDirective }],
})
export class ElTreeGridCellDefDirective extends ElCellDefDirective { }

/**
 * Header cell definition for the el-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[elTreeGridHeaderCellDef]',
  providers: [{ provide: ElCdkHeaderCellDef, useExisting: ElTreeGridHeaderCellDefDirective }],
})
export class ElTreeGridHeaderCellDefDirective extends ElHeaderCellDefDirective { }

/**
 * Footer cell definition for the el-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[elTreeGridFooterCellDef]',
  providers: [{ provide: ElCdkFooterCellDef, useExisting: ElTreeGridFooterCellDefDirective }],
})
export class ElTreeGridFooterCellDefDirective extends ElFooterCellDefDirective { }
